import React, { useCallback, useEffect, useState } from 'react';
import { AiOutlineDelete, AiOutlineDown, AiOutlineRight } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { Collapse, Button } from 'reactstrap';
import { toBrazilianFullDate } from '../../../../../CRM/shared/utils/date';

import ConfirmationModal from 'components/Modal/ConfirmationModal';
import OpportunityService from '../../../../Opportunity/services/opportunityService';
import OpportunityStageService from '../../../../OpportunityStages/service';

import './styles.css';
import { useHistory } from 'react-router';
import { InfoRelatedContainer } from '../AccountAddress/style';

const AccountOpportunities = ({ opportunities, reloadAccount }) => {
  const [activeCollapse, setActiveCollapse] = useState(false);
  const [closedCollapse, setClosedCollapse] = useState(false);
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [idOfOpportunityToRemove, setIdOfOpportunityToRemove] = useState(null);
  const [opportunityStages, setOpportunityStages] = useState(undefined);

  useEffect(() => {
    const fetchOpportunityStages = async () => {
      const recordTypeIds = [];

      for (let i = 0; i < opportunities.length; i++) {
        const id = opportunities[i].recordTypeId;
        if (!recordTypeIds.includes(id)) {
          recordTypeIds.push(id);
        }
      }

      const stages = {};

      try {
        const responses = await Promise.all(
          recordTypeIds.map((recordType) => {
            return OpportunityStageService.listOrdenade(recordType);
          })
        );

        for (let i = 0; i < recordTypeIds.length; i += 1) {
          stages[recordTypeIds[i]] = responses[i].data.data;
        }

        setOpportunityStages(stages);
      } catch {
        toast.error('Erro ao carregar as fases.', {
          closeOnClick: true,
          autoClose: 3000
        });
      }
    };

    fetchOpportunityStages();
  }, [opportunities]);

  const history = useHistory();

  const getName = useCallback((name = '') => {
    if (name.length <= 26) return name;
    return `${name.substring(0, 24).trim()}...`;
  }, []);

  const handleRemoveOpportunity = useCallback(
    (id) => {
      OpportunityService.delete(id)
        .then(() => {
          toast.success('Oportunidade removida');
          reloadAccount();
        })
        .catch((err) => {
          const message =
            err?.response?.data?.message || 'Erro ao remover oportunidade';
          toast.error(message);
        })
        .finally(() => {
          setOpenModalConfirmation(false);
          setIdOfOpportunityToRemove(null);
        });
    },
    [reloadAccount]
  );

  const controlDelete = (id) => {
    setIdOfOpportunityToRemove(id);
    setOpenModalConfirmation(true);
  };

  const handleOnOpportunityNameClick = useCallback(
    ({ recordTypeId, opportunityId }) => {
      history.push(
        `/admin/crm/opportunityEdit/${recordTypeId}/${opportunityId}`
      );
    },
    [history]
  );

  const calculateProgress = (opportunity) => {
    if (opportunityStages === undefined) {
      return 0;
    }
    const openStages = opportunityStages[opportunity.recordTypeId]
      ?.filter((stage) => stage.type === 'OPEN')
      ?.map((stage) => stage.name);

    if (openStages.indexOf(opportunity.fase) === -1) {
      return 100;
    }

    if (openStages.indexOf(opportunity.fase) === 0) {
      return 5;
    }
    return (
      ((openStages.indexOf(opportunity.fase) + 1) / (openStages.length + 1)) *
      100
    );
  };

  const renderOpportunities = (list) =>
    list.map((opportunity) => {
      const progressPercentage = calculateProgress(opportunity);

      return (
        <InfoRelatedContainer key={opportunity.id}>
          <div
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '0.1rem'
            }}
            onClick={() => controlDelete(opportunity.id)}
          >
            <AiOutlineDelete type="button" size={18} color="#82000b" />
          </div>
          <div>
            <span
              className="link-to-contact-details"
              onClick={() =>
                handleOnOpportunityNameClick({
                  recordTypeId: opportunity.recordTypeId,
                  opportunityId: opportunity.id
                })
              }
              title={opportunity.title}
            >
              {getName(opportunity.title)}
            </span>
          </div>
          <div className="small">
            <span>
              <span className="font-weight-bold">Status: </span>
              {opportunity.fase || ''}
            </span>
          </div>
          <div className="small">
            <span>
              <span className="font-weight-bold">Responsável: </span>
              {opportunity.createdByName || ''}
            </span>
          </div>
          <div className="small">
            <span>
              <span className="font-weight-bold">Data de abertura: </span>
              {toBrazilianFullDate(opportunity.created_at) || ''}
            </span>
          </div>
          <div
            className="small"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span className="font-weight-bold">Progresso:</span>
            <div
              style={{
                backgroundColor: '#e0e0e0',
                borderRadius: '0.25rem',
                height: '0.5rem',
                flex: 1,
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  width: `${progressPercentage}%`,
                  backgroundColor:
                    opportunity.fase === 'Fechado Ganho'
                      ? '#218838'
                      : opportunity.fase === 'Fechado Perdido'
                      ? '#f26464'
                      : '#11cdef',
                  height: '100%',
                  borderRadius: '0.25rem'
                }}
              ></div>
            </div>
            <span>{progressPercentage}%</span>
          </div>
        </InfoRelatedContainer>
      );
    });

  if (
    !opportunities ||
    !opportunities.length ||
    opportunityStages === undefined
  ) {
    return null;
  }

  const activeOpportunities = opportunities.filter((opportunity) => {
    if (
      opportunityStages === undefined ||
      !opportunityStages[opportunity?.recordTypeId?.toString()]
    ) {
      return false;
    }
    const openStages = opportunityStages[
      opportunity?.recordTypeId.toString()
    ].map((stage) => {
      if (stage.type === 'OPEN') {
        return stage.name;
      }
    });

    return openStages.includes(opportunity.fase);
  });

  const closedOpportunities = opportunities.filter((opportunity) => {
    if (
      opportunityStages === undefined ||
      !opportunityStages[opportunity?.recordTypeId?.toString()]
    ) {
      return false;
    }
    const closedStages = opportunityStages[
      opportunity?.recordTypeId?.toString()
    ].map((stage) => {
      if (stage.type !== 'OPEN') {
        return stage.name;
      }
    });

    return closedStages.includes(opportunity.fase);
  });

  return (
    <>
      <div>
        {/* Oportunidades Ativas */}
        <div>
          <Button
            color="link"
            onClick={() => setActiveCollapse(!activeCollapse)}
            style={{
              textAlign: 'left',
              fontWeight: 'bold',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Oportunidades Ativas ({activeOpportunities.length})
            {activeCollapse ? (
              <AiOutlineDown size={16} />
            ) : (
              <AiOutlineRight size={16} />
            )}
          </Button>
          <Collapse isOpen={activeCollapse}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {renderOpportunities(activeOpportunities)}
            </div>
          </Collapse>
        </div>

        {/* Fechado/Ganho */}
        <div style={{ marginTop: '1rem' }}>
          <Button
            color="link"
            onClick={() => setClosedCollapse(!closedCollapse)}
            style={{
              textAlign: 'left',
              fontWeight: 'bold',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Oportunidades Fechadas ({closedOpportunities.length})
            {closedCollapse ? (
              <AiOutlineDown size={16} />
            ) : (
              <AiOutlineRight size={16} />
            )}
          </Button>
          <Collapse isOpen={closedCollapse}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {renderOpportunities(closedOpportunities)}
            </div>
          </Collapse>
        </div>
      </div>

      <ConfirmationModal
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={idOfOpportunityToRemove}
        deleteResource={handleRemoveOpportunity}
        message={'Tem certeza que deseja remover esta oportunidade da conta?'}
      />
    </>
  );
};

export default AccountOpportunities;
